// src/Components/Ui/rewardFlightStore.ts
// The reward flight store: one place that turns "the player just earned N
// coins" into sprites flying from where they were earned to the matching HUD
// chip, while the HUD number holds and then ticks up as each sprite lands.
//
//   flyRewards({ kind: "coins", amount: 1200, from: orderCardRect });
//
// It is a plain module (no React) so game logic can call it from anywhere;
// Ui/RewardFlight.tsx renders the sprites and gives the HUD its held value.
//
// How the hold works: the HUD reports the number it is showing
// (reportDisplayed). The first flight for a kind freezes that number as the
// base, and while any flight of that kind is in the air the HUD shows
// base + landed instead of the real balance. When the last sprite lands the
// HUD goes back to the real balance, which by then is base + amount. So call
// flyRewards in the same tick as (or before) the balance update; the order
// inside that tick does not matter.
//
// Fallbacks: no HUD chip on screen (hidden, offscreen, not mounted), no
// origin, or prefers-reduced-motion means no sprites, just a short count-up.

export type RewardKind = "coins" | "moonrocks" | "stars";

export type RewardOrigin =
  | DOMRect
  | DOMRectReadOnly
  | { x: number; y: number }
  | Element
  | null
  | undefined;

export interface FlyRewardsOptions {
  kind: RewardKind;
  amount: number;
  /** Where the reward was earned: a rect, a point (viewport px) or an element. */
  from?: RewardOrigin;
  /** Hold the HUD now but launch the sprites after this many ms. */
  delay?: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface Flyer {
  id: number;
  kind: RewardKind;
  src: string;
  /** Keyframe points: start, burst out, curve, chip. */
  path: [Point, Point, Point, Point];
  delay: number;
  duration: number;
  size: number;
  share: number;
  /** The last sprite of a flight: its landing bounces and glows the chip. */
  last: boolean;
}

export interface Pop {
  id: number;
  kind: RewardKind;
  at: Point;
  big: boolean;
}

export interface KindSnapshot {
  active: boolean;
  base: number;
  landed: number;
  total: number;
  /** Bumps once per finished flight burst, with that burst's total. */
  finishSeq: number;
  lastTotal: number;
}

const S3 = "https://moonrock-images.s3.us-east-2.amazonaws.com/";

/** Same art as the HUD chips. */
export const REWARD_ICON: Record<RewardKind, string> = {
  coins: S3 + "GoldCoin9.png",
  moonrocks: S3 + "moonrockIcon.png",
  stars: S3 + "Star-Resource.png",
};

export const KINDS: RewardKind[] = ["coins", "moonrocks", "stars"];

/** Cap on sprites in the air at once, across every kind. */
export const MAX_FLYERS = 36;
const COUNT_UP_MS = 650;

/** 6 to 12 sprites scaled with the amount; tiny amounts get one per unit. */
export function spritesFor(amount: number): number {
  if (!(amount > 0)) return 0;
  if (amount < 6) return Math.max(1, Math.floor(amount));
  if (amount <= 50) return 6;
  if (amount <= 150) return 7;
  if (amount <= 400) return 8;
  if (amount <= 1000) return 9;
  if (amount <= 2500) return 10;
  if (amount <= 6000) return 11;
  return 12;
}

/** Split an amount into n whole shares that add up to it, larger shares first. */
export function splitAmount(amount: number, n: number): number[] {
  const total = Math.max(0, Math.floor(amount));
  const parts = Math.max(1, Math.min(n, total || 1));
  const each = Math.floor(total / parts);
  let rest = total - each * parts;
  return Array.from({ length: parts }, () => {
    const extra = rest > 0 ? 1 : 0;
    rest -= extra;
    return each + extra;
  });
}

export function toPoint(from: RewardOrigin): Point | null {
  if (!from) return null;
  if (typeof Element !== "undefined" && from instanceof Element) {
    const r = from.getBoundingClientRect();
    if (!r.width && !r.height) return null;
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }
  const anyFrom = from as any;
  if (typeof anyFrom.width === "number" && typeof anyFrom.left === "number") {
    return { x: anyFrom.left + anyFrom.width / 2, y: anyFrom.top + anyFrom.height / 2 };
  }
  if (typeof anyFrom.x === "number" && typeof anyFrom.y === "number") return { x: anyFrom.x, y: anyFrom.y };
  return null;
}

export function prefersReducedMotion(): boolean {
  try {
    return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** The HUD chip for a kind, or null when it is missing, hidden or offscreen. */
export function findTarget(kind: RewardKind): HTMLElement | null {
  if (typeof document === "undefined") return null;
  const el = document.querySelector<HTMLElement>(`[data-reward-target="${kind}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 2 || r.height < 2) return null;
  const vw = window.innerWidth || document.documentElement.clientWidth;
  const vh = window.innerHeight || document.documentElement.clientHeight;
  if (r.bottom < 0 || r.right < 0 || r.top > vh || r.left > vw) return null;
  try {
    const cs = window.getComputedStyle(el);
    if (cs.visibility === "hidden" || cs.display === "none" || (cs.opacity !== "" && Number(cs.opacity) === 0)) return null;
  } catch {
    /* ignore */
  }
  return el;
}

function targetPoint(el: HTMLElement): Point {
  const icon = el.querySelector<HTMLElement>(".mr-stat__icon") || el;
  const r = icon.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

// ─── Store ────────────────────────────────────────────────────────────

type Listener = () => void;
const listeners = new Set<Listener>();

const EMPTY: KindSnapshot = { active: false, base: 0, landed: 0, total: 0, finishSeq: 0, lastTotal: 0 };
let kinds: Record<RewardKind, KindSnapshot> = { coins: { ...EMPTY }, moonrocks: { ...EMPTY }, stars: { ...EMPTY } };
let flyers: Flyer[] = [];
let pops: Pop[] = [];
const displayed: Partial<Record<RewardKind, number>> = {};
/** Sprites and count-ups still to land, per kind. */
const pendingWork: Record<RewardKind, number> = { coins: 0, moonrocks: 0, stars: 0 };
const waiters: Record<RewardKind, Array<() => void>> = { coins: [], moonrocks: [], stars: [] };
const landTimers = new Map<number, ReturnType<typeof setTimeout>>();
let nextId = 1;

function emit() {
  listeners.forEach((l) => l());
}

export function subscribe(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export const getKind = (kind: RewardKind): KindSnapshot => kinds[kind];
export const getFlyers = (): Flyer[] => flyers;
export const getPops = (): Pop[] => pops;

/** The HUD tells the store what it is showing, so a new flight can hold it. */
export function reportDisplayed(kind: RewardKind, value: number) {
  displayed[kind] = value;
}

/** The HUD went away: forget its value so flights fall back to nothing. */
export function clearDisplayed(kind: RewardKind) {
  delete displayed[kind];
}

function patchKind(kind: RewardKind, patch: Partial<KindSnapshot>) {
  kinds = { ...kinds, [kind]: { ...kinds[kind], ...patch } };
}

function addLanded(kind: RewardKind, share: number) {
  const k = kinds[kind];
  patchKind(kind, { landed: Math.min(k.total, k.landed + share) });
}

function maybeFinish(kind: RewardKind) {
  if (pendingWork[kind] > 0) return;
  const k = kinds[kind];
  if (!k.active) return;
  patchKind(kind, { active: false, base: 0, landed: 0, total: 0, finishSeq: k.finishSeq + 1, lastTotal: k.total });
  const done = waiters[kind];
  waiters[kind] = [];
  done.forEach((r) => r());
}

/** Small WAAPI pop on the chip (transform only); a no-op without the API. */
function animateChip(kind: RewardKind, big: boolean) {
  const el = document.querySelector<HTMLElement>(`[data-reward-target="${kind}"]`);
  if (!el || typeof el.animate !== "function") return;
  const frames = big
    ? [{ transform: "scale(1)" }, { transform: "scale(1.28)" }, { transform: "scale(0.94)" }, { transform: "scale(1.06)" }, { transform: "scale(1)" }]
    : [{ transform: "scale(1)" }, { transform: "scale(1.12)" }, { transform: "scale(1)" }];
  try {
    el.animate(frames, { duration: big ? 520 : 170, easing: "cubic-bezier(0.3, 0.7, 0.3, 1.2)" });
  } catch {
    /* ignore */
  }
}

function addPop(kind: RewardKind, at: Point, big: boolean) {
  const id = nextId++;
  pops = [...pops, { id, kind, at, big }];
  window.setTimeout(() => {
    pops = pops.filter((p) => p.id !== id);
    emit();
  }, big ? 700 : 360);
}

/** A sprite reached the chip (called by the layer, or by its safety timer). */
export function landFlyer(id: number) {
  const f = flyers.find((x) => x.id === id);
  if (!f) return;
  const t = landTimers.get(id);
  if (t) clearTimeout(t);
  landTimers.delete(id);
  flyers = flyers.filter((x) => x.id !== id);
  addLanded(f.kind, f.share);
  pendingWork[f.kind] -= 1;
  const target = findTarget(f.kind);
  const at = target ? targetPoint(target) : f.path[3];
  addPop(f.kind, at, false);
  animateChip(f.kind, false);
  if (f.last) {
    addPop(f.kind, at, true);
    animateChip(f.kind, true);
  }
  maybeFinish(f.kind);
  emit();
}

const raf = (cb: (t: number) => void) =>
  typeof window.requestAnimationFrame === "function" ? window.requestAnimationFrame(cb) : window.setTimeout(() => cb(Date.now()), 16);
const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

/** No sprites: tick the held number up to its new value. */
function countUp(kind: RewardKind, amount: number) {
  pendingWork[kind] += 1;
  const start = now();
  let given = 0;
  const step = () => {
    const t = Math.min(1, (now() - start) / COUNT_UP_MS);
    const eased = 1 - (1 - t) * (1 - t);
    const target = Math.round(amount * eased);
    if (target > given) {
      addLanded(kind, target - given);
      given = target;
    }
    if (t < 1) {
      emit();
      raf(step);
      return;
    }
    pendingWork[kind] -= 1;
    animateChip(kind, true);
    maybeFinish(kind);
    emit();
  };
  raf(step);
}

const rand = (a: number, b: number) => a + Math.random() * (b - a);

function launch(kind: RewardKind, amount: number, from: RewardOrigin) {
  const target = findTarget(kind);
  const start = toPoint(from);
  const room = MAX_FLYERS - flyers.length;
  const n = Math.min(spritesFor(amount), room);
  if (!target || !start || prefersReducedMotion() || n <= 0) {
    countUp(kind, amount);
    return;
  }
  const to = targetPoint(target);
  const shares = splitAmount(amount, n);
  const vw = window.innerWidth || 400;
  const created: Flyer[] = shares.map((share, i) => {
    // Burst out around the source, upward-biased, then curve into the chip.
    const angle = rand(-Math.PI * 0.95, -Math.PI * 0.05) + (Math.random() < 0.25 ? Math.PI : 0);
    const r = rand(26, 64);
    const s0 = { x: start.x + rand(-8, 8), y: start.y + rand(-6, 6) };
    const s1 = { x: s0.x + Math.cos(angle) * r, y: s0.y + Math.sin(angle) * r * 0.8 };
    const dx = to.x - s1.x;
    const dy = to.y - s1.y;
    const len = Math.max(1, Math.hypot(dx, dy));
    const bend = rand(40, 110) * (Math.random() < 0.5 ? -1 : 1);
    const mid = {
      x: Math.max(8, Math.min(vw - 8, s1.x + dx * 0.55 + (-dy / len) * bend)),
      y: s1.y + dy * 0.55 + (dx / len) * bend,
    };
    const end = { x: to.x + rand(-2, 2), y: to.y + rand(-2, 2) };
    return {
      id: nextId++,
      kind,
      src: REWARD_ICON[kind],
      path: [s0, s1, mid, end] as [Point, Point, Point, Point],
      delay: i * 70 + rand(0, 30),
      duration: rand(720, 880),
      size: kind === "coins" ? 28 : 26,
      share,
      last: i === shares.length - 1,
    };
  });
  // The last sprite in the list must also be the last to land.
  const latest = Math.max(...created.map((f) => f.delay + f.duration));
  const lastF = created[created.length - 1];
  lastF.duration = Math.max(lastF.duration, latest - lastF.delay + 40);

  pendingWork[kind] += created.length;
  flyers = [...flyers, ...created];
  for (const f of created) {
    // Safety net: land even if the animation callback never fires (hidden tab).
    landTimers.set(f.id, setTimeout(() => landFlyer(f.id), f.delay + f.duration + 500));
  }
}

/**
 * Fly a reward to its HUD chip. Resolves when the HUD shows the new value.
 * Safe to call when the HUD is not mounted (resolves at once).
 */
export function flyRewards(opts: FlyRewardsOptions): Promise<void> {
  const { kind } = opts;
  const amount = Math.floor(Number(opts.amount) || 0);
  if (amount <= 0 || typeof window === "undefined") return Promise.resolve();
  const k = kinds[kind];
  if (!k.active) {
    const base = displayed[kind];
    if (typeof base !== "number") return Promise.resolve();
    patchKind(kind, { active: true, base, landed: 0, total: amount });
  } else {
    patchKind(kind, { total: k.total + amount });
  }
  const done = new Promise<void>((resolve) => waiters[kind].push(resolve));
  const delay = Math.max(0, opts.delay || 0);
  if (delay > 0) {
    pendingWork[kind] += 1;
    setTimeout(() => {
      pendingWork[kind] -= 1;
      launch(kind, amount, opts.from);
      emit();
    }, delay);
  } else {
    launch(kind, amount, opts.from);
  }
  emit();
  return done;
}

/** Tests only: drop every flight and timer. */
export function resetRewardFlights() {
  landTimers.forEach((t) => clearTimeout(t));
  landTimers.clear();
  flyers = [];
  pops = [];
  kinds = { coins: { ...EMPTY }, moonrocks: { ...EMPTY }, stars: { ...EMPTY } };
  KINDS.forEach((k) => {
    pendingWork[k] = 0;
    waiters[k] = [];
    delete displayed[k];
  });
  emit();
}
